import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';

/**
 * Compact dev-only timeline scrubber for `state.mode === 'simulated'`
 * (docs/live-build.md §5 "Simulation dev strip"). Replaces the old big
 * slider panel — this is chrome for inspecting the build, never shown for a
 * live build.
 */
export function SimulationStrip({
  step,
  steps,
  onStep,
}: {
  step: number;
  steps: number;
  onStep: (step: number) => void;
}): ReactElement {
  const [collapsed, setCollapsed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const reducedMotion =
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (!playing) return undefined;
    if (step >= steps - 1) {
      setPlaying(false);
      return undefined;
    }
    const timer = window.setTimeout(() => onStep(step + 1), 900);
    return () => window.clearTimeout(timer);
  }, [playing, step, steps, onStep]);

  if (steps <= 0) return <></>;

  if (collapsed) {
    return (
      <button
        type="button"
        className="simulation-strip-dot"
        onClick={() => setCollapsed(false)}
        aria-label="Expand simulation controls"
      />
    );
  }

  return (
    <div className="simulation-strip" role="group" aria-label="Simulated build timeline">
      <span className="simulation-strip-label">
        Simulated · {step + 1}/{steps}
      </span>
      <button
        type="button"
        onClick={() => {
          setPlaying(false);
          onStep(Math.max(0, step - 1));
        }}
        disabled={step === 0}
        aria-label="Previous frame"
      >
        ◀
      </button>
      <button
        type="button"
        onClick={() => {
          if (reducedMotion) return;
          setPlaying((value) => !value);
        }}
        disabled={reducedMotion}
        aria-label={playing ? 'Pause' : 'Play'}
      >
        {playing ? '❚❚' : '▶'}
      </button>
      <button
        type="button"
        onClick={() => {
          setPlaying(false);
          onStep(Math.min(steps - 1, step + 1));
        }}
        disabled={step === steps - 1}
        aria-label="Next frame"
      >
        ▶
      </button>
      <input
        type="range"
        min={0}
        max={Math.max(0, steps - 1)}
        value={step}
        onChange={(event) => {
          setPlaying(false);
          onStep(Number(event.target.value));
        }}
        aria-label="Frame"
      />
      <button
        type="button"
        onClick={() => {
          setPlaying(false);
          onStep(0);
        }}
      >
        Reset
      </button>
      <button
        type="button"
        className="simulation-strip-collapse"
        onClick={() => setCollapsed(true)}
        aria-label="Collapse simulation controls"
      >
        ×
      </button>
    </div>
  );
}
